import type { AdvisorBrand } from './advisorBrand'

export interface CycleReviewLike {
  id: number
  company_id: number
  cycle_start: string
  cycle_end: string
  review_data: any
  status: string
  created_at: string
}

export interface BuildCoverInput {
  brand: AdvisorBrand
  review: CycleReviewLike
  clientName: string
}

export interface ExportInput {
  brand: AdvisorBrand
  review: CycleReviewLike
  clientName: string
  contentNode: HTMLElement
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function cycleReviewFilename(opts: { clientName: string; cycleEnd: string }): string {
  return `cycle-review-${slugify(opts.clientName)}-${opts.cycleEnd}.pdf`
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string),
  )
}

// A4 portrait usable height after the html2pdf 10mm margins is ~277mm.
// We size the cover to fill that height so the footer can sit at the bottom
// of the cover page using flex margin-top:auto, without overlapping the
// next page (the bug when using position:absolute against the tall wrapper).
const COVER_MIN_HEIGHT_MM = 270

export function buildCoverPageNode({ brand, review, clientName }: BuildCoverInput): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'cycle-review-pdf-cover'
  wrap.style.cssText =
    `padding:32px; font-family:Arial,Helvetica,sans-serif; color:#111827; ` +
    `page-break-after:always; display:flex; flex-direction:column; ` +
    `min-height:${COVER_MIN_HEIGHT_MM}mm; box-sizing:border-box;`

  const today = new Date().toISOString().slice(0, 10)
  const logoHtml = brand.logoDataUrl
    ? `<img src="${brand.logoDataUrl}" alt="" style="max-width:160px;max-height:60px;display:block;margin-bottom:24px"/>`
    : ''

  wrap.innerHTML = `
    <div>
      ${logoHtml}
      <h1 style="font-size:28px;margin:0 0 8px">Strategic Cycle Review</h1>
      <h2 style="font-size:18px;margin:0 0 24px;color:#374151">${escapeHtml(clientName)}</h2>
      <p style="margin:0 0 4px"><strong>Cycle:</strong> ${review.cycle_start} to ${review.cycle_end}</p>
      <p style="margin:0 0 32px"><strong>Generated:</strong> ${today}</p>
      <div style="margin-top:80px">
        <p style="margin:0 0 4px;color:#6b7280;font-size:12px;text-transform:uppercase;letter-spacing:0.06em">Prepared by</p>
        <p style="margin:0;font-weight:600;font-size:16px">${escapeHtml(brand.firmName)}</p>
        ${brand.advisorName ? `<p style="margin:0;color:#374151;font-size:14px">${escapeHtml(brand.advisorName)}</p>` : ''}
      </div>
    </div>
    <div style="margin-top:auto;color:#6b7280;font-size:11px;border-top:1px solid #d8dde6;padding-top:12px">
      ${escapeHtml(brand.footerNote)}
    </div>
  `
  return wrap
}

export async function exportCycleReviewPdf(opts: ExportInput): Promise<void> {
  const { brand, review, clientName, contentNode } = opts
  const cover = buildCoverPageNode({ brand, review, clientName })

  const wrapper = document.createElement('div')
  wrapper.style.cssText = 'background:white;'
  wrapper.appendChild(cover)
  const contentClone = contentNode.cloneNode(true) as HTMLElement
  wrapper.appendChild(contentClone)

  const html2pdf = (await import('html2pdf.js')).default

  await html2pdf()
    .set({
      margin: [10, 10, 10, 10],
      filename: cycleReviewFilename({ clientName, cycleEnd: review.cycle_end }),
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: 800 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    })
    .from(wrapper)
    .save()
}
